import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBulkDropdownData } from "../../api/keyvalue";
import { Codesandbox, Search, AlertCircle } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import VehicleTypeAddComponent from "../../components/master/VehicleTypeAddComponent";
import {
  createVehicleType,
  updateVehicleType,
  searchVehicleTypes,
  getVehicleTypeById,
} from "../../api/vehicleTypeApi";
import { errorActions } from "../../store/error-slice";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";

const FORM = FORMS.VEHICLE.TYPE.form;

const VehicleType = () => {
  const user = useSelector((state) => state.user);
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.VEHICLE,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const dispatch = useDispatch();
  const errorState = useSelector(
    (state) => state.error.vehicleTypeMasterErrors,
  );

  // Search criteria states
  const [searchType, setSearchType] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);

  const [editVehicleType, setEditVehicleType] = useState(null);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdown
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const vehicleTypeRef = useRef();

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const [initialActive, setInitialActive] = useState(1);

  useEffect(() => {
    if (editMode && editVehicleType) {
      setInitialActive(editVehicleType.vhtActive);
    }
  }, [editMode, editVehicleType]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      //new

      const { active, status, vht_by_category: vehCategory } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "vht_by_category" },
      ]);

      setActiveOptions(active);
      setStatusOptions(status);
      setCategoryOptions(vehCategory);

      setIsLoading(false);
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditVehicleType(null);
    setDrawerTab(1);
    dispatch(errorActions.clearVehicleTypeError());
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    vehicleTypeRef.current.resetForm();
    setEditMode(false);
    setEditVehicleType(null);
  };

  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    setSearchSubmitLoading(true);
    setSearchClicked(true);
    setActiveAccordion1(false);
    setResetVisible(true);

    try {
      const results =
        (await searchVehicleTypes({
          vhtType: searchType,
          vhtStatus: status,
          vhtActive: active,
        })) || [];

      const mappedResults = results.map((item) => {
        const statusOpt = statusOptions.find(
          (o) =>
            Number(o.value) === Number(item.vhtStatus) ||
            o.key === String(item.vhtStatus),
        );
        const activeOpt = activeOptions.find(
          (o) =>
            Number(o.value) === Number(item.vhtActive) ||
            o.key === String(item.vhtActive),
        );
        return {
          ...item,
          statusText: statusOpt ? statusOpt.text : "Pending",
          activeText: activeOpt ? activeOpt.text : "Active",
        };
      });

      setSearchRes(mappedResults);
    } catch (error) {
      console.error(error);
      toast.error("Failed to search vehicle types", { containerId: "Main" });
    } finally {
      setSearchSubmitLoading(false);
    }
  };

  const handleSearchReset = (e) => {
    if (e) e.preventDefault();
    setSearchType("");
    setStatus("");
    setActive("");
    setSearchRes([]);
    setSearchClicked(false);
    setActiveAccordion1(true);
    setResetVisible(false);
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();

    const data = vehicleTypeRef.current.getData();
    const isDeletedActive =
      Number(data.vhtActive) === 2 && Number(initialActive) !== 2;

    if (!bypassDeleteCheck && isDeletedActive) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      deleteWarningRef.current?.showModal();
      return;
    }

    setIsSubmitLoading(true);
    dispatch(errorActions.clearVehicleTypeError());
    const vehicleTypeErrors = vehicleTypeRef.current.validateForm();

    if (Object.keys(vehicleTypeErrors).length > 0) {
      dispatch(
        errorActions.validateVehicleType({
          vehicleTypeRequest: data,
        }),
      );
      setIsSubmitLoading(false);
      toast.error("There was an error adding Vehicle Type", {
        autoClose: 5000,
        containerId: "Main",
      });
    } else {
      try {
        if (editMode) {
          await updateVehicleType({
            ...data,
            author: user?.username,
          });
          toast.success("Vehicle Type updated successfully!", {
            containerId: "Main",
            autoClose: 2000,
          });
        } else {
          await createVehicleType({
            ...data,
            author: user?.username,
          });
          toast.success("Vehicle Type created successfully!", {
            containerId: "Main",
            autoClose: 2000,
          });
        }
        handleDrawerClose();
        reset();
        handleSearchSubmit();
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 409) {
          dispatch(
            errorActions.addVehicleTypeErrorObject({
              form: "vehicleTypeMasterErrors",
              errors: {
                vhtType: "This Vehicle Type already exists.",
              },
            }),
          );
          toast.error("Vehicle Type already exists", { containerId: "Main" });
        } else {
          toast.error("An error occurred while saving", { containerId: "Main" });
        }
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };



  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            deleteWarningRef.current?.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular top attached menu">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Codesandbox size={15} style={{ marginRight: "5px" }} />
            </span>
            Vehicle Type Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            disabled={firstViableTab === -1}
            onClick={handleDrawerOpen}
            authenticated={true}
            form={FORM}
            module={MODULES.Master}
            editMode={false}
          />
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  handleSearchReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form" onSubmit={handleSearchSubmit}>
                <h4 className="ui dividing header">Vehicle Type Information</h4>
                <div className="three fields">
                  <div className="field">
                    <label>Vehicle Type</label>
                    <input
                      type="text"
                      placeholder="Vehicle Type"
                      name="vhtType"
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    className="field"
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                    <Button template="search" onClick={handleSearchSubmit} />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Id",
                    field: "vhtId",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Vehicle Type",
                    field: "vhtType",
                  },
                  {
                    headerName: "Description",
                    field: "vhtDescription",
                  },
                  {
                    headerName: "Max CBM",
                    field: "vhtMaxCbm",
                  },
                  {
                    headerName: "Carbon Factor",
                    field: "vhtCarbonFactor",
                  },
                  {
                    headerName: "Status",
                    field: "statusText",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "activeText",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  try {
                    const response = await getVehicleTypeById(props.data.vhtId);
                    if (response) {
                      setEditVehicleType(response);
                      setEditMode(true);
                      setIsDrawerOpen(true);
                    }
                  } catch (error) {
                    console.error(error);
                    toast.error("Failed to load vehicle type details", {
                      containerId: "Main",
                    });
                  } finally {
                    setSearchFetchLoading(false);
                  }
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="vehicle-type-code-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>Vehicle Type: {editVehicleType?.vhtType}</h3>
            </div>
          ) : (
            <h3>Create Vehicle Type</h3>
          )
        }
        body={
          <>
            <form className="drawer-content" onSubmit={handleSubmit}>
              <div style={{ width: "60vw", boxShadow: "none" }}>
                <div className="ui top attached tabular menu">
                  <Tab
                    Icon={Codesandbox}
                    onClick={() => {
                      setDrawerTab(1);
                    }}
                    active={drawerTab === 1}
                    errors={
                      Object.keys(errorState.vehicleTypeMasterErrors || {})
                        .length > 0
                    }
                    label="Vehicle Type Details"
                    authenticated={true}
                    form={FORM}
                    showMandatory={true}
                    isMandatory={true}
                  />
                </div>
                <div
                  className="ui bottom attached active tab segment"
                  style={{ display: drawerTab === 1 ? "block" : "none" }}
                >
                  <VehicleTypeAddComponent
                    ref={vehicleTypeRef}
                    isLoading={isLoading}
                    errors={errorState.vehicleTypeMasterErrors}
                    dropdown={{
                      statusOptions,
                      activeOptions,
                      categoryOptions,
                    }}
                    editMode={editMode}
                    currentValues={editMode ? editVehicleType : null}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    padding: "10px 0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORM}
                      module={MODULES.Master}
                      authenticated={true}
                      editMode={editMode}
                    />
                  </div>
                </div>
              </div>
            </form>
            <Modal
              ref={deleteWarningRef}
              closeButton={false}
              minWidth={"300px"}
            >
              {showDeleteWarning && deleteWarningModal}
            </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default VehicleType;
